import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import TenantCombobox from '../ui/TenantCombobox';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateAgreementModal = ({ isOpen, onClose, onSuccess, token }) => {
  const [tenants, setTenants] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [formData, setFormData] = useState({
    tenant_id: '',
    room_id: '',
    lessor_name: '',
    lessor_address: '',
    agreement_date: new Date().toISOString().split('T')[0]
  });
  const [selectedTenant, setSelectedTenant] = useState(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Auto-fill lessor details from user profile
      if (response.data.name || response.data.business_name) {
        setFormData(prev => ({
          ...prev,
          lessor_name: response.data.name || response.data.business_name || '',
          lessor_address: response.data.address || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  }, [token]);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tenants?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  }, [token]);

  const fetchRooms = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch all rooms for all properties
      const allRooms = [];
      for (const property of response.data) {
        const roomsResponse = await axios.get(`${API}/properties/${property.id}/rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allRooms.push(...roomsResponse.data);
      }
      setRooms(allRooms);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchUserProfile();
      fetchTenants();
      fetchRooms();
    }
  }, [isOpen, fetchUserProfile, fetchTenants, fetchRooms]);

  useEffect(() => {
    if (formData.tenant_id) {
      const tenant = tenants.find(t => t.id === formData.tenant_id);
      setSelectedTenant(tenant);
      if (tenant) {
        setFormData(prev => ({ ...prev, room_id: tenant.room_id }));
      }
    }
  }, [formData.tenant_id, tenants]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/agreements`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Agreement Created!",
        description: "Rent agreement has been generated successfully"
      });

      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create agreement",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  function resetForm() {
    setFormData({
      tenant_id: '',
      room_id: '',
      lessor_name: '',
      lessor_address: '',
      agreement_date: new Date().toISOString().split('T')[0]
    });
    setSelectedTenant(null);
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Rent Agreement</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Select Tenant *</Label>
              <TenantCombobox
                tenants={tenants.map(t => ({
                  ...t,
                  room_number: rooms.find(r => r.id === t.room_id)?.room_number || 'N/A',
                }))}
                value={formData.tenant_id}
                onChange={(id) => setFormData({ ...formData, tenant_id: id })}
                required
                testId="agreement-tenant-combobox"
              />
            </div>

            {selectedTenant && (
              <div className="col-span-2 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Tenant Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><span className="font-medium">Name:</span> {selectedTenant.name}</p>
                  <p><span className="font-medium">Phone:</span> {selectedTenant.phone}</p>
                  <p><span className="font-medium">Email:</span> {selectedTenant.email || 'N/A'}</p>
                  <p><span className="font-medium">Rent:</span> ₹{selectedTenant.rent_amount}/month</p>
                  <p><span className="font-medium">Deposit:</span> ₹{selectedTenant.deposit_amount}</p>
                  <p><span className="font-medium">Check-in:</span> {selectedTenant.check_in_date}</p>
                </div>
              </div>
            )}

            <div className="col-span-2">
              <Label>Lessor Name (Property Owner) *</Label>
              <Input
                value={formData.lessor_name}
                onChange={(e) => setFormData({...formData, lessor_name: e.target.value})}
                placeholder="Enter property owner name"
                required
              />
              {loadingProfile && <p className="text-xs text-gray-500 mt-1">Loading from profile...</p>}
              {!loadingProfile && formData.lessor_name && <p className="text-xs text-green-600 mt-1">✓ Auto-filled from your profile in Settings</p>}
            </div>

            <div className="col-span-2">
              <Label>Lessor Address (Property Owner Address) *</Label>
              <textarea
                value={formData.lessor_address}
                onChange={(e) => setFormData({...formData, lessor_address: e.target.value})}
                placeholder="Enter property owner address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                required
              />
              {loadingProfile && <p className="text-xs text-gray-500 mt-1">Loading from profile...</p>}
              {!loadingProfile && formData.lessor_address && <p className="text-xs text-green-600 mt-1">✓ Auto-filled from your profile in Settings</p>}
              {!loadingProfile && !formData.lessor_address && (
                <p className="text-xs text-orange-600 mt-1">💡 Tip: Save your details in Settings → Profile to auto-fill this field</p>
              )}
            </div>

            <div className="col-span-2">
              <Label>Agreement Date *</Label>
              <Input
                type="date"
                value={formData.agreement_date}
                onChange={(e) => setFormData({...formData, agreement_date: e.target.value})}
                required
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? 'Creating...' : 'Create Agreement'}
            </Button>
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAgreementModal;
